import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Utils } from '../../services';
import { focal_point_css } from '../../services/eventPoster';
import { instance_logo_url, usable_image_url } from '../../services/instanceLogo';

import './PosterListCell.scss';

const PosterListCell = ({
    posterUrl,
    poster,
    to,
    children,
    variant = 'thumbnail',
    fallbackIcon = 'film',
    fillHeight = false,
    contentPosterBackground = true,
    useInstanceLogoFallback = true,
}) => {
    const { i18n } = useTranslation();
    const setting = useSelector(state => state.settings?.setting);
    const [poster_failed, set_poster_failed] = useState(false);
    const [logo_failed, set_logo_failed] = useState(false);
    const thumb_w = variant === 'thumbnail' ? 120 : 200;
    const thumb_h = variant === 'thumbnail' ? 180 : 120;
    const object_position = focal_point_css(poster);
    const safe_poster_url = usable_image_url(posterUrl || poster?.url);
    const logo_url = useMemo(
        () => (useInstanceLogoFallback
            ? instance_logo_url(setting, i18n.language, thumb_w, thumb_h)
            : ''),
        [useInstanceLogoFallback, setting, i18n.language, thumb_w, thumb_h]
    );

    useEffect(() => {
        set_poster_failed(false);
    }, [safe_poster_url]);

    useEffect(() => {
        set_logo_failed(false);
    }, [logo_url]);

    const show_poster = safe_poster_url && !poster_failed;
    const is_logo_fallback = !show_poster && !!logo_url && !logo_failed;
    const proxied_url = show_poster
        ? Utils.imgProxyUrl(safe_poster_url, thumb_w, thumb_h)
        : (is_logo_fallback ? logo_url : null);
    const proxied_srcset = show_poster
        ? Utils.imgProxySrcset(safe_poster_url, thumb_w, thumb_h)
        : null;
    const proxied_sizes = Utils.imgProxySizes(thumb_w);
    const content_bg = contentPosterBackground && show_poster
        ? Utils.imgProxyImageSet(safe_poster_url, 400, 240)
        : null;

    const cell_class = [
        'PosterListCell',
        `PosterListCell--${variant}`,
        fillHeight ? 'PosterListCell--fillHeight' : '',
        content_bg ? 'PosterListCell--withContentBg' : '',
        is_logo_fallback ? 'PosterListCell--logo-fallback' : '',
    ].filter(Boolean).join(' ');

    const link_class = [
        'PosterListCell__link',
        fillHeight ? 'PosterListCell__link--fillHeight' : '',
    ].filter(Boolean).join(' ');

    const on_img_error = () => {
        if (show_poster)
            set_poster_failed(true);
        else if (is_logo_fallback)
            set_logo_failed(true);
    };

    const inner = (
        <div className={cell_class}>
            <div className="PosterListCell__poster" aria-hidden="true">
                {proxied_url ? (
                    variant === 'thumbnail' ? (
                        <img
                            src={proxied_url}
                            srcSet={proxied_srcset || undefined}
                            sizes={proxied_srcset ? proxied_sizes : undefined}
                            alt=""
                            className="PosterListCell__img"
                            style={show_poster ? { objectPosition: object_position } : undefined}
                            onError={on_img_error}
                        />
                    ) : (
                        <div
                            className="PosterListCell__strip-bg"
                            style={{
                                backgroundImage: is_logo_fallback
                                    ? `url('${logo_url}')`
                                    : Utils.imgProxyImageSet(safe_poster_url, thumb_w, thumb_h),
                                backgroundPosition: is_logo_fallback ? undefined : object_position,
                            }}
                        />
                    )
                ) : (
                    <div className="PosterListCell__fallback">
                        <i className={`fa fa-${fallbackIcon}`} />
                    </div>
                )}
            </div>
            <div className="PosterListCell__content">
                {content_bg && (
                    <div
                        className="PosterListCell__content-bg"
                        style={{ backgroundImage: content_bg, backgroundPosition: object_position }}
                        aria-hidden="true"
                    />
                )}
                <div className="PosterListCell__content-inner">
                    {children}
                </div>
            </div>
        </div>
    );

    if (to) {
        return (
            <Link to={to} className={link_class}>
                {inner}
            </Link>
        );
    }

    return inner;
};

export default PosterListCell;
